import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  CreditCard,
  DollarSign,
  Clock,
  CheckCircle,
  AlertCircle,
  TrendingUp,
  Calendar,
  Receipt,
  Search,
  Filter,
  RefreshCw,
  Eye,
  ArrowUpCircle,
  ArrowDownCircle,
  Wallet,
  Building,
  FileText,
  Download,
} from "lucide-react";
import { format, differenceInDays, parseISO } from "date-fns";
import apiService from "../services/api";
import LoadingSpinner from "../components/ui/LoadingSpinner";
import Button from "../components/ui/Button";
import { toast } from "react-hot-toast";

export default function CustomerPayables() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("dueDate");
  const [selectedTimeframe, setSelectedTimeframe] = useState("all");

  useEffect(() => {
    fetchPayablesData();
  }, []);

  const fetchPayablesData = async () => {
    try {
      setLoading(true);
      const data = await apiService.getCustomerInvoices();
      setInvoices(data);
    } catch (error) {
      console.error("Failed to fetch payables data:", error);
      toast.error("Failed to load payables data");
    } finally {
      setLoading(false);
    }
  };

  const calculateTotalPaid = (payments) => {
    return payments?.reduce((sum, payment) => sum + payment.amountPaid, 0) || 0;
  };

  const calculateOutstanding = (invoice) => {
    const totalPaid = calculateTotalPaid(invoice.payments);
    return Math.max(0, (invoice.totalAmount || invoice.amount) - totalPaid);
  };

  const isOverdue = (dueDate, status) => {
    if (status === "paid") return false;
    return new Date(dueDate) < new Date();
  };

  const getDaysOverdue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    return Math.max(0, differenceInDays(today, due));
  };

  const getDaysUntilDue = (dueDate) => {
    const today = new Date();
    const due = new Date(dueDate);
    return differenceInDays(due, today);
  };

  const getPaymentUrgency = (invoice) => {
    if (invoice.status === "paid") return "paid";

    const daysUntilDue = getDaysUntilDue(invoice.dueDate);
    const isOverdueStatus = isOverdue(invoice.dueDate, invoice.status);

    if (isOverdueStatus) return "overdue";
    if (daysUntilDue <= 3) return "urgent";
    if (daysUntilDue <= 7) return "due_soon";
    return "normal";
  };

  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case "paid":
        return "bg-green-100 text-green-800 border-green-200";
      case "overdue":
        return "bg-red-100 text-red-800 border-red-200";
      case "urgent":
        return "bg-orange-100 text-orange-800 border-orange-200";
      case "due_soon":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      default:
        return "bg-blue-100 text-blue-800 border-blue-200";
    }
  };

  const getUrgencyLabel = (urgency, invoice) => {
    switch (urgency) {
      case "paid":
        return "Paid";
      case "overdue":
        return `Overdue (${getDaysOverdue(invoice.dueDate)} days)`;
      case "urgent":
        return `Due in ${getDaysUntilDue(invoice.dueDate)} days`;
      case "due_soon":
        return `Due in ${getDaysUntilDue(invoice.dueDate)} days`;
      default:
        return `Due in ${getDaysUntilDue(invoice.dueDate)} days`;
    }
  };

  const filterInvoicesByTimeframe = (invoices) => {
    const now = new Date();

    switch (selectedTimeframe) {
      case "overdue":
        return invoices.filter((inv) => isOverdue(inv.dueDate, inv.status));
      case "next_7_days":
        return invoices.filter((inv) => {
          const days = getDaysUntilDue(inv.dueDate);
          return days >= 0 && days <= 7 && inv.status !== "paid";
        });
      case "next_30_days":
        return invoices.filter((inv) => {
          const days = getDaysUntilDue(inv.dueDate);
          return days >= 0 && days <= 30 && inv.status !== "paid";
        });
      case "paid":
        return invoices.filter((inv) => inv.status === "paid");
      default:
        return invoices;
    }
  };

  const filteredInvoices = filterInvoicesByTimeframe(invoices).filter(
    (invoice) => {
      const matchesSearch = invoice.invoiceNumber
        ?.toLowerCase()
        .includes(searchTerm.toLowerCase());
      const matchesStatus =
        statusFilter === "all" || invoice.status === statusFilter;
      return matchesSearch && matchesStatus;
    },
  );

  const sortedInvoices = [...filteredInvoices].sort((a, b) => {
    switch (sortBy) {
      case "dueDate":
        return new Date(a.dueDate) - new Date(b.dueDate);
      case "amount":
        return (b.totalAmount || b.amount) - (a.totalAmount || a.amount);
      case "outstanding":
        return calculateOutstanding(b) - calculateOutstanding(a);
      case "urgency":
        const urgencyOrder = {
          overdue: 0,
          urgent: 1,
          due_soon: 2,
          normal: 3,
          paid: 4,
        };
        return (
          urgencyOrder[getPaymentUrgency(a)] -
          urgencyOrder[getPaymentUrgency(b)]
        );
      default:
        return 0;
    }
  });

  // Calculate summary statistics
  const totalOutstanding = invoices.reduce(
    (sum, inv) => sum + calculateOutstanding(inv),
    0,
  );
  const totalOverdue = invoices
    .filter((inv) => isOverdue(inv.dueDate, inv.status))
    .reduce((sum, inv) => sum + calculateOutstanding(inv), 0);
  const totalDueSoon = invoices
    .filter((inv) => {
      const days = getDaysUntilDue(inv.dueDate);
      return days >= 0 && days <= 7 && inv.status !== "paid";
    })
    .reduce((sum, inv) => sum + calculateOutstanding(inv), 0);
  const totalPaid = invoices.reduce(
    (sum, inv) => sum + calculateTotalPaid(inv.payments),
    0,
  );

  const overdueCount = invoices.filter((inv) =>
    isOverdue(inv.dueDate, inv.status),
  ).length;
  const dueSoonCount = invoices.filter((inv) => {
    const days = getDaysUntilDue(inv.dueDate);
    return days >= 0 && days <= 7 && inv.status !== "paid";
  }).length;

  const PaymentUrgencyBadge = ({ invoice }) => {
    const urgency = getPaymentUrgency(invoice);
    return (
      <span
        className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium border ${getUrgencyColor(
          urgency,
        )}`}
      >
        {getUrgencyLabel(urgency, invoice)}
      </span>
    );
  };

  const PaymentHistoryCard = ({ payments, invoiceAmount }) => {
    if (!payments || payments.length === 0) {
      return (
        <div className="text-center py-4 text-gray-500">
          <Receipt className="w-8 h-8 mx-auto mb-2 text-gray-300" />
          <p className="text-sm">No payments recorded</p>
        </div>
      );
    }

    return (
      <div className="space-y-3">
        {payments.map((payment, index) => (
          <div
            key={index}
            className="flex items-center justify-between p-3 bg-green-50 rounded-lg border border-green-200"
          >
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-4 h-4 text-green-600" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-900">
                  {payment.method}
                </p>
                <p className="text-xs text-gray-500">
                  {format(new Date(payment.paymentDate), "MMM dd, yyyy")}
                </p>
              </div>
            </div>
            <div className="text-right">
              <p className="text-sm font-semibold text-green-600">
                ₹{payment.amountPaid?.toLocaleString()}
              </p>
            </div>
          </div>
        ))}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100 dark:from-gray-900 dark:to-gray-800 transition-colors duration-200">
      <div className="w-full mx-auto p-6 space-y-6">
        {/* Page Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-indigo-600 dark:from-blue-400 dark:to-indigo-400 bg-clip-text text-transparent">
              Payment Obligations
            </h1>
            <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">
              Track your payment due dates and history
            </p>
          </div>

          <div className="flex items-center gap-3">
            <Button
              onClick={fetchPayablesData}
              variant="outline"
              className="flex items-center gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              Refresh
            </Button>
          </div>
        </div>

        {/* Summary Dashboard */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8"
        >
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-orange-100 rounded-lg">
                <AlertCircle className="w-5 h-5 text-orange-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Outstanding</p>
                <p className="text-xl font-bold text-orange-600">
                  ₹{totalOutstanding.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-red-100 rounded-lg">
                <Clock className="w-5 h-5 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  Overdue ({overdueCount})
                </p>
                <p className="text-xl font-bold text-red-600">
                  ₹{totalOverdue.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-yellow-100 rounded-lg">
                <TrendingUp className="w-5 h-5 text-yellow-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">
                  Due Soon ({dueSoonCount})
                </p>
                <p className="text-xl font-bold text-yellow-600">
                  ₹{totalDueSoon.toLocaleString()}
                </p>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-green-100 rounded-lg">
                <CheckCircle className="w-5 h-5 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Total Paid</p>
                <p className="text-xl font-bold text-green-600">
                  ₹{totalPaid.toLocaleString()}
                </p>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Filters and Controls */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-xl shadow-sm border border-gray-200 p-6 mb-8"
        >
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder="Search invoices..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>

            <select
              value={selectedTimeframe}
              onChange={(e) => setSelectedTimeframe(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Timeframes</option>
              <option value="overdue">Overdue</option>
              <option value="next_7_days">Due in 7 days</option>
              <option value="next_30_days">Due in 30 days</option>
              <option value="paid">Paid</option>
            </select>

            <select
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="all">All Status</option>
              <option value="paid">Paid</option>
              <option value="partially_paid">Partially Paid</option>
              <option value="unpaid">Unpaid</option>
            </select>

            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-green-500"
            >
              <option value="urgency">Sort by Urgency</option>
              <option value="dueDate">Sort by Due Date</option>
              <option value="amount">Sort by Amount</option>
              <option value="outstanding">Sort by Outstanding</option>
            </select>
          </div>
        </motion.div>

        {/* Payables List */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="space-y-4"
        >
          <AnimatePresence>
            {sortedInvoices.map((invoice, index) => (
              <motion.div
                key={invoice.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: 20 }}
                transition={{ delay: index * 0.05 }}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden"
              >
                <div className="p-6">
                  <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between mb-4">
                    <div className="flex items-center gap-4">
                      <div className="p-3 bg-blue-100 rounded-lg">
                        <FileText className="w-6 h-6 text-blue-600" />
                      </div>
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {invoice.invoiceNumber}
                        </h3>
                        <p className="text-sm text-gray-500">
                          Order #{invoice.order?.id}
                        </p>
                      </div>
                    </div>
                    <PaymentUrgencyBadge invoice={invoice} />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-6">
                    <div className="text-center p-4 bg-gray-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">
                        Invoice Amount
                      </p>
                      <p className="text-xl font-bold text-gray-900">
                        ₹
                        {(
                          invoice.totalAmount || invoice.amount
                        )?.toLocaleString()}
                      </p>
                    </div>

                    <div className="text-center p-4 bg-green-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Amount Paid</p>
                      <p className="text-xl font-bold text-green-600">
                        ₹
                        {calculateTotalPaid(invoice.payments)?.toLocaleString()}
                      </p>
                    </div>

                    <div className="text-center p-4 bg-orange-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Outstanding</p>
                      <p className="text-xl font-bold text-orange-600">
                        ₹{calculateOutstanding(invoice)?.toLocaleString()}
                      </p>
                    </div>

                    <div className="text-center p-4 bg-blue-50 rounded-lg">
                      <p className="text-sm text-gray-500 mb-1">Due Date</p>
                      <p className="text-lg font-semibold text-gray-900">
                        {format(new Date(invoice.dueDate), "MMM dd, yyyy")}
                      </p>
                      <p className="text-xs text-gray-500">
                        {invoice.paymentTerms}
                      </p>
                    </div>
                  </div>

                  {invoice.payments && invoice.payments.length > 0 && (
                    <div>
                      <h4 className="text-sm font-medium text-gray-900 mb-3">
                        Payment History
                      </h4>
                      <PaymentHistoryCard
                        payments={invoice.payments}
                        invoiceAmount={invoice.totalAmount || invoice.amount}
                      />
                    </div>
                  )}
                </div>
              </motion.div>
            ))}
          </AnimatePresence>

          {sortedInvoices.length === 0 && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
              <Wallet className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">
                No payables found
              </h3>
              <p className="text-gray-500">
                {searchTerm ||
                statusFilter !== "all" ||
                selectedTimeframe !== "all"
                  ? "Try adjusting your search or filter criteria"
                  : "You have no outstanding payment obligations"}
              </p>
            </div>
          )}
        </motion.div>
      </div>
    </div>
  );
}
